import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Mail, Send, Eye, MousePointerClick, XCircle, TrendingUp, Users, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="flex-1 min-w-[140px] px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100"
      data-testid={`sp-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-xl font-bold ${accent || 'text-[#0F2040]'} mt-0.5`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function RankingList({ title, icon: Icon, items, emptyNote }) {
  return (
    <div className="flex-1 min-w-[260px]">
      <div className="text-xs font-semibold text-[#0F2040] mb-2 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic">{emptyNote}</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={it.user_id || it.org_id || i}
              className="flex items-center gap-3 px-2 py-1.5 rounded border border-slate-100 bg-white">
              <span className="w-4 text-[10px] text-slate-400 text-right">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-800 truncate">{it.name}</div>
                <div className="text-[10px] text-slate-400">
                  {it.sent} sent &middot; {it.opened} opened &middot; {it.clicked} clicked
                </div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 px-1.5 py-0 text-[10px]">
                {it.open_rate}% open
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Tiny 14-day bar sparkline of "sent" counts with an overlay for opens.
 */
function Sparkline({ trend }) {
  const max = Math.max(1, ...trend.map(d => d.sent));
  return (
    <div className="flex items-end gap-0.5 h-12" data-testid="sp-sparkline">
      {trend.map(d => {
        const sh = (d.sent / max) * 40;
        const oh = d.sent ? ((d.opened / d.sent) * sh) : 0;
        return (
          <div key={d.day} className="flex-1 relative" title={`${d.day}: ${d.sent} sent, ${d.opened} opened`}>
            <div className="w-full bg-slate-200 rounded-sm" style={{ height: `${sh || 2}px` }} />
            <div className="w-full bg-[#4ABFCF] absolute bottom-0 rounded-sm" style={{ height: `${oh}px` }} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Scope Pack Insights — org-wide send→open→click funnel card for the
 * Insights dashboard. Pulls /api/scope-pack/insights which aggregates
 * db.scope_pack_sends over a configurable window (default 30 days).
 */
export function ScopePackInsightsCard({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/scope-pack/insights`, {
          params: { days }, withCredentials: true,
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Card data-testid="scope-pack-insights-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
            <Mail className="w-4 h-4" /> Scope Pack Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400">Loading…</CardContent>
      </Card>
    );
  }
  if (!data || !data.totals || data.totals.sent === 0) {
    // Hide entirely until there's at least one send — no empty noise on the dashboard.
    return null;
  }

  const t = data.totals;
  return (
    <Card data-testid="scope-pack-insights-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
          <Mail className="w-4 h-4" /> Scope Pack Engagement
          <Badge variant="secondary" className="ml-1 text-[10px] bg-slate-100 text-slate-600 font-normal">
            last {data.window_days}d
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPI row */}
        <div className="flex flex-wrap gap-2">
          <Kpi icon={Send}              label="Sent"      value={t.sent} />
          <Kpi icon={Eye}               label="Opened"    value={t.opened}
               sub={`${t.open_rate}% open rate`}   accent="text-sky-700" />
          <Kpi icon={MousePointerClick} label="Clicked"   value={t.clicked}
               sub={`${t.click_rate}% click rate`} accent="text-indigo-700" />
          <Kpi icon={XCircle}           label="Bounced"   value={t.bounced}
               sub={t.bounce_rate ? `${t.bounce_rate}% bounce rate` : ''}
               accent={t.bounced ? 'text-rose-700' : undefined} />
        </div>

        {/* 14-day trend */}
        <div>
          <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> 14-day send activity (teal = opens)
          </div>
          <Sparkline trend={data.trend_14d || []} />
        </div>

        {/* Rankings */}
        <div className="flex flex-wrap gap-6 pt-2 border-t border-slate-100">
          <RankingList title="Top consultants by open rate" icon={Users}
            items={data.top_consultants || []}
            emptyNote="No consultant activity yet." />
          <RankingList title="Most engaged organizations"   icon={Building2}
            items={data.top_organizations || []}
            emptyNote="No organization activity yet." />
        </div>
      </CardContent>
    </Card>
  );
}
